import { supabase } from "@/utils/supabase";
import { OrderDetailsMapper } from "@/adapters/OrderDetails.mapper";
/**
 * Concrete implementation reading from:
 * - v_order_details view
 * - order_statuses table
 * - route_points → charging_sessions (linkage via route_point_id)
 *
 * Services THROW — stores catch with onError().
 */
export class OrderDetailsService {
    /**
     * Load full order details from the v_order_details view.
     */
    async getOrderDetails(id) {
        const { data, error } = await supabase()
            .from("v_order_details")
            .select("*")
            .eq("id", id)
            .single();
        if (error) {
            throw new Error(`Failed to load order details: ${error.message}`);
        }
        if (!data) {
            throw new Error(`Order details not found for id: ${id}`);
        }
        return OrderDetailsMapper.toDomain(data);
    }
    /**
     * Load status history for an order (ordered chronologically).
     */
    async getStatusHistory(id) {
        const { data, error } = await supabase()
            .from("order_statuses")
            .select("*")
            .eq("idOrder", id)
            .eq("isDeleted", false)
            .order("createdAt", { ascending: true });
        if (error) {
            throw new Error(`Failed to load status history: ${error.message}`);
        }
        return (data ?? []).map(OrderDetailsMapper.statusHistoryToDomain);
    }
    /**
     * Load charging sessions for an order.
     * LINKAGE: charging_sessions has no idOrder — find via route_points.idOrder.
     * 1. Fetch route_point ids where idOrder = orderId
     * 2. Fetch charging_sessions where route_point_id in (those ids)
     */
    async getActiveChargingSessions(id) {
        // Step 1: get route_point ids for this order
        const { data: routePoints, error: rpError } = await supabase()
            .from("route_points")
            .select("id")
            .eq("idOrder", id);
        if (rpError) {
            throw new Error(`Failed to load route points for sessions: ${rpError.message}`);
        }
        if (!routePoints || routePoints.length === 0) {
            return [];
        }
        const routePointIds = routePoints.map((rp) => rp.id);
        // Step 2: fetch charging sessions for those route points
        const { data: sessions, error: sessionError } = await supabase()
            .from("charging_sessions")
            .select("*")
            .in("route_point_id", routePointIds)
            .order("created_at", { ascending: false });
        if (sessionError) {
            throw new Error(`Failed to load charging sessions: ${sessionError.message}`);
        }
        return (sessions ?? []).map(OrderDetailsMapper.chargingSessionToDomain);
    }
}
export const orderDetailsService = new OrderDetailsService();
